import { useState } from "react"
import { Alert, Button, Typography } from "@mui/material"
import { PostBotPageLayout } from "../components/common/postBotPageLayout"
import { skipToken } from "@reduxjs/toolkit/query"
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom"
import type { TemplateWriteAccess } from "../hooks/useVersionDraft"
import type { Placement } from "../interface/schedule"
import { useGetPlacementQuery } from "../services/publications/placementsApi"
import { PlacementForm } from "../components/placements/placementForm"
import { PostBotNavigation } from "../components/common/postBotNavigation"
import { TemplateQueryFeedback } from "../components/templates/templateQueryFeedback"
import { placementPath } from "../utils/placementDraft"
import { placementHistoryPath } from "../utils/occurrences"

export interface PlacementPageProps extends TemplateWriteAccess {
  placementId?: string
}
export default function PlacementPage(props: PlacementPageProps) {
  const params = useParams()
  const id = props.placementId ?? params.placementId ?? params.id ?? "new"
  return <PlacementDetail key={id} {...props} placementId={id} />
}
function PlacementDetail({
  placementId: id,
  ...access
}: PlacementPageProps & { placementId: string }) {
  const [search] = useSearchParams()
  const navigate = useNavigate()
  const creating = id === "new"
  const valid =
    creating || (/^[1-9][0-9]*$/.test(id) && BigInt(id) <= 2147483647n)
  const query = useGetPlacementQuery(creating || !valid ? skipToken : id)
  const detail =
    !query.error && query.currentData?.id === id ? query.currentData : undefined
  const [initial, setInitial] = useState<Placement>()
  if (!initial && detail && !creating) setInitial(detail)
  return (
    <PostBotPageLayout>
      <PostBotNavigation />
      <Button
        component={Link}
        to="/telegram/post-bot/placements"
        sx={{ alignSelf: "flex-start" }}
      >
        Библиотека размещений
      </Button>
      <Typography variant="h5">
        {creating ? "Новое размещение" : `Размещение ${id}`}
      </Typography>
      {!valid && <Alert severity="error">Некорректный ID размещения</Alert>}
      {!creating && valid && (
        <Button component={Link} to={placementHistoryPath(id)}>
          История отправок
        </Button>
      )}
      {!creating && valid && (
        <TemplateQueryFeedback
          query={query}
          empty={!initial && !detail && "Размещение не найдено"}
        />
      )}
      {valid && (creating || initial) && (
        <PlacementForm
          {...access}
          initial={initial}
          channelId={
            creating ? (search.get("channelId") ?? undefined) : undefined
          }
          publicationId={
            creating ? (search.get("publicationId") ?? undefined) : undefined
          }
          onSaved={(saved) => {
            if (creating) navigate(placementPath(saved.id), { replace: true })
          }}
        />
      )}
    </PostBotPageLayout>
  )
}
